<template>
  <v-card class="pr-5" flat>
    <v-card-title>
      <v-row>
        <v-col cols="11">
          <h6 class="title">
            Trial Notifications
          </h6>
        </v-col>
        <v-col cols="1">
          <clear-trial-notifications-dialog class="float-right" />
        </v-col>
      </v-row>
    </v-card-title>
    <v-card-text class="mr-0 pr-0 mt-0 pt-0" style="overflow-y: auto;">
      <v-data-table
        :headers="headers"
        :items="items"
        item-key="trialId"
        sort-by="trialExternalId"
        group-by="overallParentTrial.id"
        :custom-sort="sortTrialNotifications"
      >
        <template #group.header="{ group }">
          <template v-if="group">
            <td>{{ getParentTrialDisplayName(group) }}</td>
            <td></td>
            <td>{{ getParentTrialSponsorName(group) }}</td>
            <td></td>
            <td></td>
            <td></td>
          </template>
        </template>
        <template #item.trialExternalId="{ item }">
          <div :class="['trial-name', item.overallParentTrial ? 'pl-4' : '']">
            <trial-status-icon v-if="item.status" class="pr-5" :status="item.status" />
            <span v-if="item.link">
              <a :href="item.link" target="_blank" class="viper-link">{{ item.trialExternalId }}</a>
            </span>
            <span v-else>{{ item.trialExternalId }}</span>
          </div>
        </template>
        <template #item.prefs.internalNotifications="{ item }">
          <v-switch
            v-model="item.prefs.internalNotifications"
            @change="updatePreferences(item.prefs)"
          />
        </template>
        <template #item.prefs.immediateEmailNotifications="{ item }">
          <v-switch
            v-model="item.prefs.immediateEmailNotifications"
            @change="updatePreferences(item.prefs)"
          />
        </template>
        <template #item.prefs.dailyDigestEmailNotifications="{ item }">
          <v-switch
            v-model="item.prefs.dailyDigestEmailNotifications"
            @change="updatePreferences(item.prefs)"
          />
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
import ClearTrialNotificationsDialog from '@/components/manage/ClearTrialNotificationsDialog'
import TrialStatusIcon from '@/components/trials/TrialStatusIcon'
import { mapGetters } from 'vuex'
import { MyActiveOrganization, MyUser } from '@/helpers/my-account-helper'
import _ from 'lodash'

export default {
  components: { ClearTrialNotificationsDialog, TrialStatusIcon },
  data() {
    return {
      headers: [
        { text: 'Trial', value: 'trialExternalId', width: '10%' },
        { text: 'Trial (Internal)', value: 'name', width: '15%' },
        { text: 'Sponsor', value: 'sponsor', width: '15%' },
        {
          text: 'Internal',
          value: 'prefs.internalNotifications',
          width: '10%'
        },
        {
          text: 'Email Immediately',
          value: 'prefs.immediateEmailNotifications',
          width: '10%'
        },
        {
          text: 'Email Daily',
          value: 'prefs.dailyDigestEmailNotifications',
          width: '10%'
        }
      ],
      items: [],
      parentTrialMap: {}
    }
  },
  computed: {
    ...mapGetters(['getOrganizationTrialViewModel', 'trialNotificationPreferencesByTrialId']),
    trials() {
      return _.map(this.getOrganizationTrialViewModel(), function(it) {
        return {
          trialId: _.get(it, 'trial.id'),
          trialExternalId: _.get(it, 'trial.externalId'),
          name: _.get(it, 'externalId') || _.get(it, 'trial.id'),
          sponsorName: _.get(it, 'trial.sponsor.name', null),
          link: _.get(it, 'trial.trialLink', null),
          overallParentTrial: _.get(it, 'trial.overallParentTrial', null),
          type: _.get(it, 'trial.type', null),
          status: _.get(it, 'latestStatus.value', null)
        }
      })
    }
  },
  async created() {
    await this.$store.dispatch('GET_TRIALS')
    this.setItems()
  },
  methods: {
    setItems() {
      this.items = _.map(this.trials, it => {
        const parentTrial = it.overallParentTrial
        if (parentTrial && _.isNil(this.parentTrialMap[parentTrial.id])) {
          this.parentTrialMap[parentTrial.id] = {
            trialExternalId: parentTrial.externalId,
            sponsor: parentTrial.sponsor?.name,
            trialId: parentTrial.id
          }
        }
        return {
          trialId: it.trialId,
          trialExternalId: it.trialExternalId,
          name: it.name,
          sponsor: it.sponsorName,
          disease: it.disease,
          prefs: this.trialNotificationPreferencesByTrialId(it.trialId) || {
            trial: { id: it.trialId, type: 'TRIAL' }
          },
          link: it.link,
          overallParentTrial: parentTrial,
          status: it.status
        }
      })
    },
    async updatePreferences(preferences) {
      if (_.isNil(preferences.id)) {
        await this.$store.dispatch('CREATE_TRIAL_NOTIFICATION_PREFERENCES', {
          trialNotificationPreferences: {
            ...preferences,
            ...{ user: MyUser, organization: MyActiveOrganization }
          }
        })
        await this.$store.dispatch('GET_TRIAL_NOTIFICATION_PREFERENCES')
        this.setItems()
      } else {
        await this.$store.dispatch('UPDATE_TRIAL_NOTIFICATION_PREFERENCES', {
          trialNotificationPreferences: {
            ...preferences,
            ...{ user: MyUser, organization: MyActiveOrganization }
          }
        })
      }
      this.setItems()
    },
    getParentTrialDisplayName(trialId) {
      return this.parentTrialMap[trialId]?.trialExternalId
    },
    getParentTrialSponsorName(trialId) {
      return this.parentTrialMap[trialId]?.sponsor
    },
    sortTrialNotifications(items, sortBy, sortDesc) {
      const sortField = _.last(sortBy)
      const sortOrder = _.last(sortDesc) ? 'desc' : 'asc'
      const shouldIncludeParentInSort = _.includes(
        ['sponsor', 'name', 'trialExternalId'],
        sortField
      )
      if (shouldIncludeParentInSort) {
        const parentTrials = _.values(this.parentTrialMap)
        const [cohorts, simpleTrials] = _.partition(items, t => t.overallParentTrial)
        const topLevelItems = _.concat(parentTrials, simpleTrials)
        const sortedItems = _.orderBy(topLevelItems, [sortField], [sortOrder])
        const groupedCohorts = _.groupBy(cohorts, c => c.overallParentTrial.id)
        Object.keys(groupedCohorts).forEach(parentTrialId => {
          const parentIndex = _.findIndex(sortedItems, i => i.trialId === parentTrialId)
          const sortedCohorts = _.orderBy(groupedCohorts[parentTrialId], [sortField], [sortOrder])
          sortedItems.splice(parentIndex, 1, ...sortedCohorts)
        })
        return sortedItems
      } else {
        return _.orderBy(items, item => _.get(item, sortField, ''), [sortOrder])
      }
    }
  }
}
</script>
<style lang="scss" scoped>
::v-deep .v-row-group__header {
  background: none !important;
}

.trial-name {
  display: flex;
  span {
    align-self: center;
  }
}
</style>
